import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseView } from '@/components/base'
import { StickyHeaderNavigation } from '@/components/StickyHeaderNavigation'
import { EmptyKemCard } from '@/components/EmptyKemCard'
import { Modal } from '@/components/Modal'
import { Info } from '@/assets/icons'
import {
  PersonalInfos,
  KemCardTermsCheckboxGroup,
  CreateKemCardButton,
  KemCardDetailsBSChildren,
  kemCardTermsChecklist,
} from '@/features/kem-card/info-confirmation'
import useOffsets from '@/hooks/useOffsets'
import { useAppAnalytics } from '@/services/analytics'

export const KemCardInfoConfirmationScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const offsets = useOffsets()
  const { trackEvent, events } = useAppAnalytics()

  React.useEffect(() => {
    trackEvent(events.CARD_ACTIVATION_STARTED, {
      card_type: 'virtual',
    })
  }, [trackEvent, events])

  const [acceptedTerms, setAcceptedTerms] = useState<boolean[]>(
    Array(kemCardTermsChecklist.length).fill(false),
  )
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)

  const allTermsAccepted = acceptedTerms.every(Boolean)

  const openDetailsModal = () => {
    setIsDetailsModalOpen(true)
  }

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false)
  }

  return (
    <>
      {/* Sticky Header Navigation with Info Icon */}
      <StickyHeaderNavigation
        title={t('screens:confirmYourLegalName.title')}
        onBackPress={() => navigate(-1)}
        rightIcon={
          <BaseView
            onClick={openDetailsModal}
            style={{
              cursor: 'pointer',
              padding: '4px',
              paddingRight: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: '100',
            }}
          >
            <Info size={24} color="#6B7280" />
          </BaseView>
        }
      />

      <BaseView
        flex={1}
        px={12}
        style={{
          backgroundColor: '#131415',
          minHeight: '100vh',
          paddingTop: offsets.top + 50, // Space for sticky header
          paddingBottom: offsets.bottom + 60,
        }}
      >
        {/* Main Content */}
        <BaseView mt={19} gap={20} style={{ width: '100%', maxWidth: '100%' }}>
          {/* Kem Card Preview - empty card with image only */}
          <EmptyKemCard
            containerStyle={{
              maxWidth: '100%',
              margin: '0 auto',
            }}
          />

          {/* Personal Information */}
          <PersonalInfos />
        </BaseView>

        {/* Bottom Section with Terms and Button */}
        <BaseView gap={24} mt={19} pb={19}>
          {/* Terms and Conditions Checkboxes */}
          <KemCardTermsCheckboxGroup
            acceptedTerms={acceptedTerms}
            setAcceptedTerms={setAcceptedTerms}
          />

          {/* Create Card Button */}
          <CreateKemCardButton disabled={!allTermsAccepted} />
          {/* Bottom Section with Terms and Button */}
        </BaseView>

        {/* Details Modal */}
        <Modal
          isOpen={isDetailsModalOpen}
          onClose={closeDetailsModal}
          style={{
            height: '100vh',
            top: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          <KemCardDetailsBSChildren onConfirm={closeDetailsModal} />
        </Modal>
      </BaseView>
    </>
  )
}
